import axios from 'axios';

import { MessageDTO, DeleteMessageDTO, MessageAttachmentDTO } from '../models/MessageDTO';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';
// Allowed file types for message attachments
export const MESSAGE_ALLOWED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

// Max file size for message attachments (5MB)
export const MESSAGE_MAX_FILE_SIZE = 5 * 1024 * 1024;

export const messageApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/message`,
  withCredentials: true,
});

installSessionInterceptors(messageApi);

export const sendMessage = async (message: MessageDTO) => {
  try {
    const response = await messageApi.post('/send', message);

    return response.data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const receiveMessages = async () => {
  try {
    const response = await messageApi.get('/receive');

    return response.data;
  } catch (error) {
    console.error('Error receiving messages:', error);
    throw error;
  }
};

export const getChats = async () => {
  try {
    const response = await messageApi.get('/chats');

    return response.data as MessageDTO[];
  } catch (error) {
    // 403 is expected for PracticeAdmin users (HA-37)
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      return [] as MessageDTO[];
    }
    console.error('Error getting chats:', error);
    throw error;
  }
};

export const getChat = async (chatId: string) => {
  try {
    const response = await messageApi.get(`/chat/${chatId}`);

    return response.data;
  } catch (error) {
    console.error('Error getting chat:', error);
    throw error;
  }
};

export const createChat = async (chat: MessageDTO) => {
  try {
    const response = await messageApi.post('/chat', chat);

    return response.data as MessageDTO;
  } catch (error) {
    console.error('Error creating chat:', error);
    throw error;
  }
};

export const deleteMessage = async (deleteRequest: DeleteMessageDTO) => {
  try {
    const response = await messageApi.delete('/message', { data: deleteRequest });

    return response.data;
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

// Message Attachment Functions

export interface MessageUploadUrlRequest {
  fileName: string;
  fileType: string;
  fileSize: number;
}

export interface MessageUploadUrlResponse {
  uploadUrl: string;
  attachmentId: string;
  expiresAt: string;
}

/**
 * Validates a file for message attachment
 * @returns null if valid, error message if invalid
 */
export const validateMessageAttachment = (file: File): string | null => {
  if (!MESSAGE_ALLOWED_FILE_TYPES.includes(file.type)) {
    return `File type not allowed. Allowed types: PNG, JPG, PDF, DOCX`;
  }

  if (file.size > MESSAGE_MAX_FILE_SIZE) {
    const maxSizeMB = MESSAGE_MAX_FILE_SIZE / (1024 * 1024);

    return `File too large. Maximum size is ${maxSizeMB}MB`;
  }

  return null;
};

/**
 * Get a presigned URL for uploading an attachment
 */
export const getMessageUploadUrl = async (
  request: MessageUploadUrlRequest
): Promise<MessageUploadUrlResponse> => {
  try {
    const response = await messageApi.post('/attachments/upload-url', request);

    return response.data;
  } catch (error) {
    console.error('Error getting upload URL:', error);
    throw error;
  }
};

/**
 * Upload a file directly to S3 using a presigned URL
 * @returns Progress percentage (0-100) via onProgress callback
 */
export const uploadFileToPresignedUrl = async (
  presignedUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);

        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

/**
 * Confirm that an upload has completed
 */
export const confirmMessageUpload = async (attachmentId: string): Promise<MessageAttachmentDTO> => {
  try {
    const response = await messageApi.post('/attachments/confirm', { attachmentId });

    return response.data;
  } catch (error) {
    console.error('Error confirming upload:', error);
    throw error;
  }
};

/**
 * Upload a message attachment (combines get URL, upload, and confirm)
 */
export const uploadMessageAttachment = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<MessageAttachmentDTO> => {
  // Validate file
  const validationError = validateMessageAttachment(file);

  if (validationError) {
    throw new Error(validationError);
  }

  // Get presigned URL
  const uploadRequest: MessageUploadUrlRequest = {
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
  };

  const { uploadUrl, attachmentId } = await getMessageUploadUrl(uploadRequest);

  // Upload to S3
  await uploadFileToPresignedUrl(uploadUrl, file, onProgress);

  // Confirm upload
  return confirmMessageUpload(attachmentId);
};

/**
 * Get the attachment type category from MIME type
 */
export const getAttachmentType = (mimeType: string): 'image' | 'document' | 'audio' | 'video' => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';

  return 'document';
};

/**
 * Format file size for display
 */
export const formatAttachmentSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};
